import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, UserPen, Search, Sparkles, Crown, UserRound, Check, RotateCcw, History,
} from 'lucide-react';

/**
 * EditAuthorDialog — admin dialog to reassign a story's authorship.
 *
 * Behaviour:
 *  - Shows the current author prominently at the top.
 *  - Lets the admin search registered authors (admins + Official Intimate +
 *    `is_author=True` users). Selecting one calls the reassign endpoint;
 *    the backend automatically sets `submitted_by_community` accordingly
 *    and logs the change.
 *  - "Voltar a Intimate/Administrador" reverts ownership to the current admin
 *    account and clears the community flag.
 *  - Shows the authorship audit history for this story at the bottom.
 *
 * Props:
 *   open, onOpenChange: dialog control
 *   story: { id, author_id, author_name, submitted_by_community }
 *   onSaved(updatedStory): callback after a successful reassignment
 */
export default function EditAuthorDialog({ open, onOpenChange, story, onSaved }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const loadResults = useCallback(async (term) => {
    setLoadingResults(true);
    try {
      const { data } = await api.get('/admin/users/authors', { params: { search: term, limit: 50 } });
      setResults(data || []);
    } catch { /* ignore */ } finally {
      setLoadingResults(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setShowLog(false);
    loadResults('');
    // Preload log
    if (story?.id) {
      api.get(`/admin/stories/${story.id}/authorship-log`)
        .then(({ data }) => setLogs(data || []))
        .catch(() => setLogs([]));
    }
  }, [open, story?.id, loadResults]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => loadResults(search), 250);
    return () => clearTimeout(t);
  }, [search, open, loadResults]);

  const reassign = async (authorId) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/stories/${story.id}/author`, { author_id: authorId });
      toast.success(authorId ? 'Autor atribuído' : 'Voltou a Intimate/Administrador');
      onSaved?.(data);
      // Refresh log inline
      try {
        const { data: newLog } = await api.get(`/admin/stories/${story.id}/authorship-log`);
        setLogs(newLog || []);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a atualizar.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('pt-PT', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-author-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <UserPen className="w-5 h-5 text-ruby" />
            Atribuir autor ao conto
          </DialogTitle>
          <DialogDescription>
            Reassigna este conto a um autor registado. Fica automaticamente na secção da comunidade e no perfil do autor.
          </DialogDescription>
        </DialogHeader>

        {/* Current author */}
        <div className="rounded-lg border border-border bg-background/40 p-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Autor atual</p>
          <div className="flex items-center gap-2">
            {story?.submitted_by_community ? (
              <UserRound className="w-4 h-4 text-purple-300" />
            ) : (
              <Crown className="w-4 h-4 text-gold" />
            )}
            <span className="font-medium text-foreground">{story?.author_name || 'Sem autor'}</span>
            <span className="text-xs text-muted-foreground">
              · {story?.submitted_by_community ? 'Comunidade' : 'Intimate / Administrador'}
            </span>
          </div>
        </div>

        {/* Revert to admin */}
        <Button
          variant="outline"
          onClick={() => reassign('')}
          disabled={saving || !story?.submitted_by_community}
          data-testid="revert-to-admin-btn"
          className="border-gold/40 text-gold hover:bg-gold/10 w-full justify-start"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Voltar a Intimate / Administrador
        </Button>

        {/* Search */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Procurar autor por nome, nickname ou email…"
              className="pl-9 bg-background border-border"
              data-testid="reassign-search-input"
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-border bg-background/30 divide-y divide-border/60">
            {loadingResults && (
              <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> A procurar…
              </div>
            )}
            {!loadingResults && results.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground">Sem resultados.</div>
            )}
            {!loadingResults && results.map((u) => {
              const isCurrent = u.id === story?.author_id;
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => !isCurrent && !saving && reassign(u.id)}
                  disabled={isCurrent || saving}
                  data-testid={`reassign-pick-${u.id}`}
                  className={
                    'w-full text-left flex items-center gap-3 p-3 transition-colors ' +
                    (isCurrent ? 'bg-ruby/5 cursor-default' : 'hover:bg-ruby/10 cursor-pointer')
                  }
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-ruby/40 to-black flex items-center justify-center border border-border shrink-0">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold text-white">{(u.display_name || '?').charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground truncate">{u.display_name}</span>
                      {u.is_admin && (
                        <span className="text-[10px] bg-gold/20 text-gold px-1.5 py-0.5 rounded uppercase tracking-wider">Admin</span>
                      )}
                      {!u.is_admin && u.is_official && (
                        <span className="text-[10px] bg-gold/15 text-gold px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-0.5">
                          <Sparkles className="w-2.5 h-2.5" /> Oficial
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {u.email} · {u.story_count} contos
                    </div>
                  </div>
                  {isCurrent && <Check className="w-4 h-4 text-ruby shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* History */}
        {logs.length > 0 && (
          <div className="border-t border-border pt-3">
            <button
              type="button"
              onClick={() => setShowLog((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              data-testid="toggle-authorship-log"
            >
              <History className="w-3 h-3" />
              {showLog ? 'Ocultar' : 'Ver'} histórico de alterações ({logs.length})
            </button>
            {showLog && (
              <ol className="mt-2 space-y-1.5 text-xs text-muted-foreground max-h-32 overflow-y-auto pr-1">
                {logs.map((l) => (
                  <li key={l.id} className="border-l-2 border-border pl-2">
                    <div>
                      <strong className="text-foreground">{l.prev_author_name}</strong> → <strong className="text-foreground">{l.new_author_name}</strong>
                    </div>
                    <div className="text-[10px] opacity-80">
                      {formatDate(l.changed_at)} · por {l.changed_by_admin_name}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
